import type { GeoJSONSource, MapMouseEvent } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import type { TerritoryDto } from '@rw/shared';
import { Spinner } from '../../components/ui';
import type { LoadedRegion } from '../../services/runtime';
import { useTerritoryLive } from '../../stores/territory-live';
import { addNavigationControl, createGameMap, type MapHandle, type MarkerLike } from './map-lib';
import {
  EMPTY_FC,
  LOST_COLOR,
  MINE_COLOR,
  PALETTES,
  type MapPalette,
  cellsFeatureCollection,
  circlePolygon,
  keysFeatureCollection,
  outsideMask,
  VN_ARCHIPELAGO_AREA,
  VN_ARCHIPELAGOS,
  EAST_SEA,
  EAST_SEA_BASE_NAMES,
  toExpression,
  routeFeature,
  type MapViewMode,
} from './layers';
import { t } from '../../i18n';
import { useMapTheme } from '../../stores/map-theme';

export interface GameMapProps {
  region: LoadedRegion;
  owners: Map<string, TerritoryDto>;
  myUserId: string | null;
  position?: { lat: number; lng: number; accuracy: number } | null;
  follow?: boolean;
  route?: Array<Array<[number, number]>>;
  routeVersion?: number;
  /** Cells to highlight (e.g. provisional captures during a run). */
  highlight?: string[];
  /** Cells the viewer lost (red). */
  lost?: string[];
  /** Cells the route passed through (outlined). */
  visited?: string[];
  /** Sponsored cells: brand colour, and the brand's logo when zoomed in. */
  sponsored?: SponsoredCell[];
  /** Called with the sponsored cells on screen after the view settles (for view counts). */
  onSponsoredVisible?: (gridKeys: string[]) => void;
  /** Show start/finish markers at the route ends. */
  routeEnds?: boolean;
  /** Colour territory by runner (default) or by club. */
  viewMode?: MapViewMode;
  myClubId?: string | null;
  selected?: string | null;
  onSelect?: (gridKey: string | null) => void;
  fitRoute?: boolean;
  initialZoom?: number;
  /** Where to open the map when the player's position is unknown. */
  initialCenter?: { lat: number; lng: number } | null;
  controls?: boolean;
  /** Hide everything outside the region and keep the view inside it (default). Off for routes run elsewhere. */
  clampToRegion?: boolean;
  className?: string;
  onReady?: (handle: MapHandle) => void;
}

export interface SponsoredCell {
  gridKey: string;
  color: string;
  brandId: string;
  logoUrl: string | null;
}

const GRID_MIN_ZOOM = 12;
/** One breath of a sponsored cell's glow. */
const SPONSOR_PULSE_MS = 1600;
const GIFT_ICON = 'rw-gift';
const LOGO_MIN_ZOOM = 15;
const MAX_CELLS = 5000;

/** A new base map means a new map instance: remount when the player switches light/dark. */
export function GameMap(props: GameMapProps) {
  const theme = useMapTheme((s) => s.theme);
  return <GameMapView key={theme} {...props} />;
}

function GameMapView(props: GameMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<MapHandle | null>(null);
  const markerRef = useRef<MarkerLike | null>(null);
  const propsRef = useRef(props);
  propsRef.current = props;
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const liveVersion = useTerritoryLive((s) => s.version);

  // ── create map once ──
  useEffect(() => {
    let cancelled = false;
    const el = containerRef.current!;
    const p = propsRef.current;
    const c = p.position ?? p.initialCenter ?? { lat: p.region.dto.grid.originLat, lng: p.region.dto.grid.originLng };
    const center: [number, number] = [c.lng, c.lat];
    const theme = useMapTheme.getState().theme;
    createGameMap({ container: el, center, zoom: p.initialZoom ?? 14, theme })
      .then(async (handle) => {
        if (cancelled) {
          handle.map.remove();
          return;
        }
        handleRef.current = handle;
        setupLayers(handle, p.region, p.clampToRegion !== false, PALETTES[theme]);
        if (p.controls !== false) await addNavigationControl(handle);
        handle.map.on('moveend', () => refreshCells());
        handle.map.on('click', (e: MapMouseEvent) => {
          const { region, onSelect } = propsRef.current;
          if (!onSelect) return;
          const key = region.grid.keyAt({ lat: e.lngLat.lat, lng: e.lngLat.lng });
          onSelect(region.isPlayable(key) ? key : null);
        });
        setReady(true);
        p.onReady?.(handle);
      })
      .catch((e: Error) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
      markerRef.current?.remove();
      handleRef.current?.map.remove();
      handleRef.current = null;
    };
     
  }, []);

  function refreshCells() {
    const h = handleRef.current;
    if (!h) return;
    const { region, owners, myUserId, viewMode, myClubId } = propsRef.current;
    const zoom = h.map.getZoom();
    let keys: Iterable<string>;
    if (zoom >= GRID_MIN_ZOOM) {
      const b = h.map.getBounds();
      const visible = region.grid.cellsInBBox(
        { minLat: b.getSouth(), maxLat: b.getNorth(), minLng: b.getWest(), maxLng: b.getEast() },
        MAX_CELLS,
      );
      keys = visible.filter((k) => region.isPlayable(k));
    } else {
      keys = [...owners.keys()];
    }
    setData(h, 'cells', cellsFeatureCollection(region.grid, keys, owners, myUserId, viewMode, myClubId));
    const { sponsored, onSponsoredVisible } = propsRef.current;
    if (onSponsoredVisible && sponsored?.length && zoom >= GRID_MIN_ZOOM) {
      const onScreen = new Set(keys);
      const seen = sponsored.filter((c) => onScreen.has(c.gridKey)).map((c) => c.gridKey);
      if (seen.length) onSponsoredVisible(seen);
    }
  }

  // ownership changes
  useEffect(() => {
    if (ready) refreshCells();
     
  }, [ready, props.owners, props.myUserId, props.viewMode, props.myClubId, liveVersion]);

  // sponsored cells and their brands' logos
  useEffect(() => {
    const h = handleRef.current;
    if (!ready || !h) return;
    const cells = props.sponsored ?? [];
    const grid = props.region.grid;
    setData(h, 'sponsored', {
      type: 'FeatureCollection',
      features: cells.map((c) => ({ type: 'Feature', properties: { key: c.gridKey, color: c.color }, geometry: { type: 'Polygon', coordinates: [grid.polygonOf(c.gridKey)] } })),
    });
    setData(h, 'sponsored-badges', {
      type: 'FeatureCollection',
      features: cells.map((c) => {
        const p = grid.centerOf(c.gridKey);
        return { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [p.lng, p.lat] } };
      }),
    });
    let cancelled = false;
    const withLogo = cells.filter((c) => c.logoUrl);
    const showLogos = () =>
      !cancelled &&
      setData(h, 'sponsored-logos', {
        type: 'FeatureCollection',
        features: withLogo
          .filter((c) => h.map.hasImage(logoImageId(c)))
          .map((c) => {
            const p = grid.centerOf(c.gridKey);
            return { type: 'Feature', properties: { icon: logoImageId(c) }, geometry: { type: 'Point', coordinates: [p.lng, p.lat] } };
          }),
      });
    const missing = [...new Map(withLogo.filter((c) => !h.map.hasImage(logoImageId(c))).map((c) => [logoImageId(c), c])).values()];
    showLogos();
    void Promise.all(missing.map((c) => addLogoImage(h, logoImageId(c), c.logoUrl!).catch(() => undefined))).then(showLogos);
    if (propsRef.current.onSponsoredVisible) refreshCells();
    return () => {
      cancelled = true;
    };
     
  }, [ready, props.sponsored, props.region]);

  // sponsored cells breathe: the glow brightens and fades (still for "reduce motion", paused while hidden)
  const hasSponsored = !!props.sponsored?.length;
  useEffect(() => {
    const h = handleRef.current;
    if (!ready || !h || !hasSponsored) return;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (still) return;
    let raf = 0;
    let last = 0;
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      if (document.hidden || now - last < 66) return;
      last = now;
      const k = (Math.sin((now / SPONSOR_PULSE_MS) * Math.PI * 2) + 1) / 2;
      try {
        h.map.setPaintProperty('sponsored-glow', 'line-opacity', 0.25 + 0.75 * k);
        h.map.setPaintProperty('sponsored-glow', 'line-width', 6 + 10 * k);
        h.map.setPaintProperty('sponsored-fill', 'fill-opacity', 0.12 + 0.18 * k);
      } catch {
        cancelAnimationFrame(raf);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ready, hasSponsored]);

  // flashes from realtime capture events
  useEffect(() => {
    const h = handleRef.current;
    if (!ready || !h) return;
    const { flashes } = useTerritoryLive.getState();
    let raf = 0;
    const tick = () => {
      const now = Date.now();
      const live = flashes.filter((f) => now - f.at < 4000);
      const fc = keysFeatureCollection(props.region.grid, []);
      fc.features = live.map((f) => ({
        type: 'Feature',
        properties: { key: f.gridKey, color: f.color, alpha: Math.max(0, 1 - (now - f.at) / 4000) },
        geometry: { type: 'Polygon', coordinates: [props.region.grid.polygonOf(f.gridKey)] },
      }));
      setData(h, 'flash', fc);
      if (live.length) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [ready, liveVersion, props.region]);

  // highlight + selection
  useEffect(() => {
    const h = handleRef.current;
    if (!ready || !h) return;
    setData(h, 'highlight', keysFeatureCollection(props.region.grid, props.highlight ?? []));
    setData(h, 'visited', keysFeatureCollection(props.region.grid, props.visited ?? []));
    setData(h, 'lost', keysFeatureCollection(props.region.grid, props.lost ?? []));
    setData(h, 'selected', keysFeatureCollection(props.region.grid, props.selected ? [props.selected] : []));
  }, [ready, props.highlight, props.visited, props.lost, props.selected, props.region]);

  // route
  useEffect(() => {
    const h = handleRef.current;
    if (!ready || !h) return;
    setData(h, 'route', routeFeature(props.route ?? []));
    const lines = props.route ?? [];
    const first = lines[0]?.[0];
    const lastLine = lines[lines.length - 1];
    const last = lastLine?.[lastLine.length - 1];
    setData(
      h,
      'ends',
      props.routeEnds && first && last
        ? {
            type: 'FeatureCollection',
            features: [
              { type: 'Feature', properties: { kind: 'start' }, geometry: { type: 'Point', coordinates: first } },
              { type: 'Feature', properties: { kind: 'end' }, geometry: { type: 'Point', coordinates: last } },
            ],
          }
        : EMPTY_FC,
    );
    if (props.fitRoute && props.route?.length) {
      const all = props.route.flat();
      const lngs = all.map((c) => c[0]);
      const lats = all.map((c) => c[1]);
      h.map.fitBounds(
        [
          [Math.min(...lngs), Math.min(...lats)],
          [Math.max(...lngs), Math.max(...lats)],
        ],
        { padding: 56, maxZoom: 15, duration: 0 },
      );
    }
     
  }, [ready, props.routeVersion, props.route, props.fitRoute, props.routeEnds]);

  // player position
  useEffect(() => {
    const h = handleRef.current;
    if (!ready || !h) return;
    const pos = props.position;
    if (!pos) {
      markerRef.current?.remove();
      markerRef.current = null;
      setData(h, 'accuracy', EMPTY_FC);
      return;
    }
    if (!markerRef.current) {
      const el = document.createElement('div');
      el.className = 'rw-player';
      el.setAttribute('data-testid', 'player-marker');
      markerRef.current = h.createMarker({ element: el }).setLngLat([pos.lng, pos.lat]).addTo(h.map);
    } else markerRef.current.setLngLat([pos.lng, pos.lat]);
    setData(h, 'accuracy', circlePolygon(pos.lat, pos.lng, Math.min(pos.accuracy, 500)));
    if (props.follow) h.map.easeTo({ center: [pos.lng, pos.lat], duration: 600 });
  }, [ready, props.position, props.follow]);

  return (
    <div className={`overflow-hidden bg-ink-950 ${props.className ?? 'relative'}`}>
      {/* Inline position: map libraries' unlayered CSS (position: relative) would beat Tailwind utilities. */}
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} data-testid="game-map" data-ready={ready ? 'true' : 'false'} />
      {!ready && !error ? (
        <div className="absolute inset-0 grid place-items-center text-ink-400">
          <div className="flex flex-col items-center gap-2">
            <Spinner className="size-8 text-volt" />
            <span className="text-sm">{t('Loading map…')}</span>
          </div>
        </div>
      ) : null}
      {error ? (
        <div className="absolute inset-0 grid place-items-center p-6 text-center text-sm text-ink-400">
          Map unavailable ({error}). Your run is still being recorded.
        </div>
      ) : null}
    </div>
  );
}

function setData(h: MapHandle, id: string, data: unknown) {
  const src = h.map.getSource(id) as GeoJSONSource | undefined;
  src?.setData(data as never);
}

const logoImageId = (c: SponsoredCell) => `brand-logo-${c.brandId}-${c.logoUrl}`;

/** Loads a brand logo (same origin) into the map's sprite, fitted inside 120×60 px at 2× density. */
async function addLogoImage(h: MapHandle, id: string, url: string) {
  const img = new Image();
  img.src = url;
  await img.decode();
  if (h.map.hasImage(id)) return;
  const scale = Math.min(240 / img.naturalWidth, 120 / img.naturalHeight, 1);
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const ht = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = ht;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.drawImage(img, 0, 0, w, ht);
  h.map.addImage(id, ctx.getImageData(0, 0, w, ht), { pixelRatio: 2 });
}

/** A gift badge (drawn once on a canvas) marking sponsored cells before their logos show. */
function addGiftIcon(h: MapHandle) {
  if (h.map.hasImage(GIFT_ICON)) return;
  const size = 96;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 4, 0, Math.PI * 2);
  ctx.fillStyle = '#0a0e13';
  ctx.fill();
  ctx.lineWidth = 6;
  ctx.strokeStyle = '#ffc53d';
  ctx.stroke();
  ctx.font = `${Math.round(size * 0.5)}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('🎁', size / 2, size / 2 + 3);
  h.map.addImage(GIFT_ICON, ctx.getImageData(0, 0, size, size), { pixelRatio: 2 });
}

function setupLayers(h: MapHandle, region: LoadedRegion, clamp: boolean, pal: MapPalette) {
  const m = h.map;
  const add = (id: string, data: unknown = EMPTY_FC) => m.addSource(id, { type: 'geojson', data: data as never });
  add('region', { type: 'Feature', properties: {}, geometry: region.dto.boundary });
  add('outside', outsideMask(region.dto.boundary));
  add('cells');
  add('sponsored');
  add('sponsored-logos');
  add('sponsored-badges');
  addGiftIcon(h);
  add('flash');
  add('highlight');
  add('visited');
  add('lost');
  add('ends');
  add('selected');
  add('accuracy');
  add('route');

  // The game map is the region only: everything else is hidden, and the view can't leave it.
  if (clamp) {
    m.addLayer({ id: 'outside-fill', type: 'fill', source: 'outside', paint: { 'fill-color': pal.outside, 'fill-opacity': 1 } });
    const pad = 0.05;
    m.setMaxBounds([
      [region.bbox.minLng - pad, region.bbox.minLat - pad],
      [region.bbox.maxLng + pad, region.bbox.maxLat + pad],
    ]);
  }
  m.addLayer({
    id: 'region-line',
    type: 'line',
    source: 'region',
    paint: { 'line-color': MINE_COLOR, 'line-opacity': 0.35, 'line-width': 2, 'line-dasharray': [2, 2] },
  });
  m.addLayer({
    id: 'cells-fill',
    type: 'fill',
    source: 'cells',
    paint: {
      'fill-color': ['get', 'color'],
      'fill-opacity': ['match', ['get', 'state'], 'mine', pal.ownedFill + 0.02, 'enemy', pal.ownedFill - 0.02, 0.03],
    },
  });
  m.addLayer({
    id: 'cells-line',
    type: 'line',
    source: 'cells',
    paint: {
      'line-color': ['match', ['get', 'state'], 'neutral', pal.neutralLine, ['get', 'color']],
      'line-width': ['match', ['get', 'state'], 'neutral', 0.8, 1.6],
      'line-opacity': ['match', ['get', 'state'], 'neutral', 1, 0.9],
    },
  });
  m.addLayer({
    id: 'sponsored-fill',
    type: 'fill',
    source: 'sponsored',
    paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.2 },
  });
  m.addLayer({
    id: 'visited-line',
    type: 'line',
    source: 'visited',
    paint: { 'line-color': '#38bdf8', 'line-width': 1.6, 'line-opacity': 0.75 },
  });
  m.addLayer({
    id: 'highlight-fill',
    type: 'fill',
    source: 'highlight',
    paint: { 'fill-color': MINE_COLOR, 'fill-opacity': 0.25 },
  });
  m.addLayer({
    id: 'highlight-line',
    type: 'line',
    source: 'highlight',
    paint: { 'line-color': MINE_COLOR, 'line-width': 2.5, 'line-dasharray': [1.5, 1] },
  });
  m.addLayer({
    id: 'lost-fill',
    type: 'fill',
    source: 'lost',
    paint: { 'fill-color': LOST_COLOR, 'fill-opacity': 0.3 },
  });
  m.addLayer({
    id: 'lost-line',
    type: 'line',
    source: 'lost',
    paint: { 'line-color': LOST_COLOR, 'line-width': 2.5, 'line-dasharray': [1.5, 1] },
  });
  m.addLayer({
    id: 'flash-fill',
    type: 'fill',
    source: 'flash',
    paint: { 'fill-color': ['get', 'color'], 'fill-opacity': ['*', 0.85, ['get', 'alpha']] },
  });
  m.addLayer({
    id: 'flash-line',
    type: 'line',
    source: 'flash',
    paint: { 'line-color': '#ffffff', 'line-width': 3, 'line-opacity': ['get', 'alpha'] },
  });
  // Sponsored cells sit on top of every owner's colour: a pulsing glow (animated in GameMap), a
  // bright outline, a gift badge when zoomed out and the brand's logo when zoomed in.
  m.addLayer({
    id: 'sponsored-glow',
    type: 'line',
    source: 'sponsored',
    paint: { 'line-color': ['get', 'color'], 'line-width': 10, 'line-blur': 6, 'line-opacity': 0.7 },
  });
  m.addLayer({
    id: 'sponsored-line',
    type: 'line',
    source: 'sponsored',
    paint: { 'line-color': ['get', 'color'], 'line-width': 3, 'line-opacity': 1 },
  });
  m.addLayer({
    id: 'sponsored-edge',
    type: 'line',
    source: 'sponsored',
    paint: { 'line-color': '#ffffff', 'line-width': 1, 'line-opacity': 0.85, 'line-dasharray': [2, 2] },
  });
  m.addLayer({
    id: 'sponsored-badge',
    type: 'symbol',
    source: 'sponsored-badges',
    maxzoom: LOGO_MIN_ZOOM,
    layout: {
      'icon-image': GIFT_ICON,
      'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 0.35, LOGO_MIN_ZOOM, 0.6],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });
  m.addLayer({
    id: 'sponsored-logo',
    type: 'symbol',
    source: 'sponsored-logos',
    minzoom: LOGO_MIN_ZOOM,
    layout: {
      'icon-image': ['get', 'icon'],
      'icon-size': ['interpolate', ['linear'], ['zoom'], LOGO_MIN_ZOOM, 0.45, 17, 1],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });
  m.addLayer({
    id: 'selected-line',
    type: 'line',
    source: 'selected',
    paint: { 'line-color': pal.selected, 'line-width': 3 },
  });
  m.addLayer({
    id: 'accuracy-fill',
    type: 'fill',
    source: 'accuracy',
    paint: { 'fill-color': '#38bdf8', 'fill-opacity': 0.12 },
  });
  m.addLayer({
    id: 'route-glow',
    type: 'line',
    source: 'route',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': MINE_COLOR, 'line-width': 12, 'line-opacity': 0.18, 'line-blur': 4 },
  });
  if (pal.routeCasing) {
    m.addLayer({
      id: 'route-casing',
      type: 'line',
      source: 'route',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': pal.routeCasing, 'line-width': 7.5 },
    });
  }
  m.addLayer({
    id: 'route-line',
    type: 'line',
    source: 'route',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': MINE_COLOR, 'line-width': 4.5 },
  });
  m.addLayer({
    id: 'route-ends',
    type: 'circle',
    source: 'ends',
    paint: {
      'circle-radius': 7,
      'circle-color': ['match', ['get', 'kind'], 'start', '#22c55e', '#ff4d6d'],
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 2.5,
    },
  });
  localNames(h);
  addArchipelagos(h, pal);
}

/** Base maps label places in English (unaccented in Việt Nam); runners know them by their Vietnamese names. */
function localNames(h: MapHandle) {
  const m = h.map;
  for (const layer of m.getStyle()?.layers ?? []) {
    if (layer.type !== 'symbol') continue;
    const field = m.getLayoutProperty(layer.id, 'text-field') as unknown;
    if (field == null) continue;
    const json = JSON.stringify(field);
    if (!json.includes('name_en')) continue;
    m.setLayoutProperty(layer.id, 'text-field', JSON.parse(json.replaceAll('"name_en"', '"name"').replaceAll('{name_en}', '{name}')) as never);
  }
}

/**
 * Hoàng Sa and Trường Sa are always named as Vietnamese territory when the map is zoomed out far
 * enough to show them, and the sea is Biển Đông; base-map labels in the archipelagos' waters, and the
 * base map's own name for the sea, are hidden so they can't contradict ours.
 */
function addArchipelagos(h: MapHandle, pal: MapPalette) {
  const m = h.map;
  const ours = new Set(['outside', 'region', 'cells', 'sponsored', 'sponsored-logos', 'sponsored-badges', 'flash', 'highlight', 'visited', 'lost', 'ends', 'selected', 'accuracy', 'route', 'archipelagos', 'east-sea']);
  const notThere = [
    'all',
    ['!', ['within', VN_ARCHIPELAGO_AREA]],
    ['!', ['match', ['coalesce', ['get', 'name_en'], ''], EAST_SEA_BASE_NAMES, true, false]],
    ['!', ['match', ['coalesce', ['get', 'name'], ''], EAST_SEA_BASE_NAMES, true, false]],
  ];
  for (const layer of m.getStyle()?.layers ?? []) {
    if (layer.type !== 'symbol' || ours.has((layer as { source?: string }).source ?? '')) continue;
    const filter = m.getFilter(layer.id);
    m.setFilter(layer.id, (filter ? ['all', toExpression(filter), notThere] : notThere) as never);
  }
  m.addSource('archipelagos', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: VN_ARCHIPELAGOS.map((a) => ({
        type: 'Feature',
        properties: { label: `${t(a.name)}\n(${a.province}, Việt Nam)` },
        geometry: { type: 'Point', coordinates: [a.lng, a.lat] },
      })),
    } as never,
  });
  m.addSource('east-sea', {
    type: 'geojson',
    data: { type: 'Feature', properties: { label: t(EAST_SEA.name) }, geometry: { type: 'Point', coordinates: [EAST_SEA.lng, EAST_SEA.lat] } } as never,
  });
  m.addLayer({
    id: 'east-sea-label',
    type: 'symbol',
    source: 'east-sea',
    maxzoom: 8,
    layout: {
      'text-field': ['get', 'label'],
      'text-font': h.lib === 'mapbox' ? ['DIN Pro Italic', 'Arial Unicode MS Regular'] : ['Montserrat Medium Italic', 'Open Sans Italic', 'Noto Sans Regular'],
      'text-size': 16,
      'text-letter-spacing': 0.2,
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: { 'text-color': pal.seaLabel, 'text-halo-color': pal.labelHalo, 'text-halo-width': 1 },
  });
  m.addLayer({
    id: 'archipelagos-dot',
    type: 'circle',
    source: 'archipelagos',
    paint: { 'circle-radius': 3, 'circle-color': pal.label, 'circle-opacity': 0.8 },
  });
  m.addLayer({
    id: 'archipelagos-label',
    type: 'symbol',
    source: 'archipelagos',
    layout: {
      'text-field': ['get', 'label'],
      'text-font': h.lib === 'mapbox' ? ['DIN Pro Medium', 'Arial Unicode MS Regular'] : ['Montserrat Medium', 'Open Sans Bold', 'Noto Sans Regular'],
      'text-size': 12,
      'text-offset': [0, 0.8],
      'text-max-width': 20,
      'text-anchor': 'top',
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: { 'text-color': pal.label, 'text-halo-color': pal.labelHalo, 'text-halo-width': 1.5 },
  });
}
